/**
 * Landlord Account Slide-over Alpine.js Component
 *
 * Displays landlord accounting details: balance held, rent outstanding,
 * supplier costs, reserve, rent-this-month table, transactions, and
 * supplier invoices with tabbed navigation and infinite scroll.
 */

import { legacyImportMixin } from './legacy_import_mixin'
import { transactionSearchMixin } from './transaction_search_mixin'
import { statementModalMixin } from './statement_modal_mixin'
import { landlordInvoiceMixin } from './landlord_invoice_mixin'

export function landlordAccountSlideover() {
  return {
    ...legacyImportMixin('landlord'),
    ...transactionSearchMixin('landlord'),
    ...statementModalMixin(),
    ...landlordInvoiceMixin(),

    // State
    open: false,
    aiPanelOpen: false,
    loading: false,
    loadingMore: false,
    error: null,
    zIndex: 100010,

    // Data
    landlordId: null,
    landlord: null,
    stats: null,
    rentThisMonth: [],
    transactions: [],

    // Pagination (transactions)
    page: 1,
    hasMore: false,

    // Kebab menu
    menuOpen: false,
    showBackToTop: false,

    init() {
      window.addEventListener('refresh-landlord-account', (e) => {
        if (this.open && String(this.landlordId) === String(e.detail?.id)) {
          this.refreshData()
        }
      })
      window.addEventListener('scroll-to-transaction', (e) => {
        if (this.open && e.detail?.id) this.scrollToTransaction(e.detail.id)
      })
    },

    // Open — receives { id } from event detail
    openSlideover(detail) {
      const id = detail?.id
      if (!id) return

      const store = Alpine.store?.('slideovers')

      // Bring to front if already open for this landlord
      if (store?.isOpen?.('landlord-account', id)) {
        store.bringToFront('landlord-account', id)
        return
      }

      // Get next z-index
      if (store?.getNextZIndex) {
        this.zIndex = store.getNextZIndex()
      }

      this.landlordId = id
      this.open = true
      this.menuOpen = false
      this.acctSubTab = 'transactions'
      this.clearSearch()
      this.transactions = []
      this.rentThisMonth = []
      this.page = 1
      this.hasMore = false

      // Register
      if (store?.register) {
        store.register('landlord-account', id, this)
      }

      this.fetchData(true)
    },

    // Close the slideover
    close() {
      const store = Alpine.store?.('slideovers')
      if (store && this.landlordId) {
        store.unregister('landlord-account', this.landlordId)
      }

      this.menuOpen = false
      this.$nextTick(() => {
        this.open = false
        window.dispatchEvent(new CustomEvent('account-slideover-closed'))
      })
    },

    // Fetch data from controller
    async fetchData(initial = false) {
      if (initial) {
        this.loading = true
        this.page = 1
        this.transactions = []
      } else {
        this.loadingMore = true
      }
      this.error = null

      try {
        const params = new URLSearchParams()
        params.set('page', this.page)

        const response = await fetch(`/accounting/accounts/landlords/${this.landlordId}.json?${params}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        this.landlord = data.landlord
        this.stats = data.stats
        this.rentThisMonth = data.rent_this_month || []
        this.landlordInvoices = data.invoices || []

        if (initial) {
          this.transactions = data.transactions?.lines || []
        } else {
          this.transactions = [...this.transactions, ...(data.transactions?.lines || [])]
        }

        this.hasMore = data.transactions?.pagination?.has_more || false
      } catch (error) {
        console.error('Failed to fetch landlord account:', error)
        this.error = 'Failed to load landlord account details. Please try again.'
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    // Silent refresh — keeps existing content visible while fetching updated data
    async refreshData() {
      this.error = null

      try {
        const params = new URLSearchParams()
        params.set('page', 1)

        const response = await fetch(`/accounting/accounts/landlords/${this.landlordId}.json?${params}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

        const data = await response.json()
        this.landlord = data.landlord
        this.stats = data.stats
        this.rentThisMonth = data.rent_this_month || []
        this.landlordInvoices = data.invoices || []
        this.transactions = data.transactions?.lines || []
        this.page = 1
        this.hasMore = data.transactions?.pagination?.has_more || false
      } catch (error) {
        console.error('Failed to refresh landlord account:', error)
      }
    },

    // Load next page (transactions only)
    loadMore() {
      if (this.loadingMore || !this.hasMore) return
      this.page++
      this.fetchData(false)
    },

    // Infinite scroll handler
    handleScroll(event) {
      this.showBackToTop = window.shouldShowBackToTop(event)

      const el = event.target
      const scrollPosition = el.scrollTop + el.clientHeight
      const threshold = el.scrollHeight - 200

      if (scrollPosition >= threshold && this.hasMore && !this.loadingMore) {
        this.loadMore()
      }
    },

    scrollToTop() {
      window.scrollToTopHelper(this.$refs.scrollContainer)
    },

    // Dynamic text size for stat cards
    get statValueClass() {
      if (!this.stats) return 'text-2xl'
      const lengths = [
        this.formatPence(this.stats.balance_held_pence).length,
        this.formatPence(this.stats.rent_outstanding_pence).length,
        this.formatPence(this.stats.reserve_pence).length
      ]
      const max = Math.max(...lengths)
      if (max > 9) return 'text-base'
      if (max > 7) return 'text-lg'
      return 'text-2xl'
    },

    // Stat card subtitle text
    get balanceSubtitle() {
      if (!this.stats) return ''
      if (this.stats.balance_held_pence < 0) return 'Money is owed'
      if (this.stats.balance_held_pence === 0) return 'Nothing to pay'
      return 'Held on account'
    },

    get rentOutstandingSubtitle() {
      if (!this.stats) return ''
      const count = this.stats.rent_outstanding_property_count || 0
      const total = this.stats.rent_outstanding_total_properties || 0
      if (this.stats.rent_outstanding_pence === 0) return 'All received'
      return `${count} of ${total} ${total === 1 ? 'property' : 'properties'}`
    },

    get reserveSubtitle() {
      if (!this.stats) return ''
      return this.stats.reserve_pence === 0 ? 'No reserve held' : 'Withheld for costs'
    },

    // Current month/year display
    get currentMonthYear() {
      const date = new Date()
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
      return `${months[date.getMonth()]} ${date.getFullYear()}`
    },

    // Format helpers
    formatPence(pence) {
      if (pence === null || pence === undefined) return '-'
      const isNegative = pence < 0
      const absPence = Math.abs(pence)
      const pounds = (absPence / 100).toFixed(2)
      const formatted = parseFloat(pounds).toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
      return (isNegative ? '-' : '') + '\u00A3' + formatted
    },

    formatSignedPence(pence) {
      if (pence === null || pence === undefined) return '-'
      const prefix = pence >= 0 ? '+' : '-'
      const absPence = Math.abs(pence)
      const pounds = (absPence / 100).toFixed(2)
      const formatted = parseFloat(pounds).toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
      return `${prefix}\u00A3${formatted}`
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr + 'T00:00:00')
      const day = date.getDate()
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${day} ${months[date.getMonth()]} ${date.getFullYear()}`
    },

    txnBarClass(txn) {
      if (txn.payment_status === 'unreconciled') return 'bg-amber-400'
      if (txn.charge_status === 'unsettled' || txn.charge_status === 'outstanding' || txn.charge_status === 'partial') return 'bg-amber-400'
      return 'bg-blue-400'
    },

    amountColorClass(pence) {
      if (pence > 0) return 'text-emerald-600 dark:text-emerald-400'
      if (pence < 0) return 'text-red-600 dark:text-red-400'
      return 'text-gray-500 dark:text-gray-400'
    }
  }
}
